// @ts-strict-ignore
/* tslint:disable:max-line-length */
import {
  AfterViewChecked,
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SecurityContext,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { AppConstants, LMTimeStampDirective, LMTransformOptions } from '@utils/index';
import { LMModel, LMModelChangedEvent, LMModelField } from '@form/model';
import {
  SohoAlertDirective,
  SohoBusyIndicatorDirective,
  SohoTooltipDirective,
  SohoTrackDirtyDirective
} from 'ids-enterprise-ng';
import { LMBaseComponent } from '@base/lm-base.component';
import { LMFieldServiceAdapter } from './lm-field-service-adapter';
import { BehaviorSubject, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LMLayoutType } from './services';
import { LMKeyType } from '@services/lm-keyboard-shortcut.service';
import { LMLookupComponent } from '@field/lookup';

export class LMFieldValueChangedEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  bindId: string;

  constructor(event: Event, type: LMFieldEventType, bindId: string) {
    this.event = event;
    this.eventType = type;
    this.bindId = bindId;
  }
}

export class LMTemplateFieldValueChangedEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  bindId: string;

  constructor(event: Event, type: LMFieldEventType, bindId: string) {
    this.event = event;
    this.eventType = type;
    this.bindId = bindId;
  }
}

export class LMInvalidFieldEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  bindId: string;
  panelId: string;
  alert: SohoAlertDirective;

  constructor(event: Event, type: LMFieldEventType, bindId: string, sohoAlert: SohoAlertDirective) {
    this.event = event;
    this.eventType = type;
    this.bindId = bindId;
    this.alert = sohoAlert;
  }
}

export class LMCompactFormEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  fieldId: string;
  layout: LMFormLayout;
  title: LMMessageRule;
  moreSpecFields: LMSpecFields;
  originalCompactFormLayout: LMCompoundFieldLayout;
  displayOnly: boolean;
  excludeOriginalField: boolean;

  constructor(
    event: Event,
    fieldId: string,
    title: LMMessageRule,
    layout: LMFormLayout,
    moreSpecFields?: LMSpecFields,
    originalFieldLayout?: LMCompoundFieldLayout,
    displayOnly = false,
    excludeOriginalField = false
  ) {
    this.event = event;
    this.eventType = 'OpenCompactFormEvent';
    this.fieldId = fieldId;
    this.title = title;
    this.layout = layout;
    this.moreSpecFields = moreSpecFields;
    this.originalCompactFormLayout = originalFieldLayout;
    this.displayOnly = displayOnly;
    this.excludeOriginalField = excludeOriginalField;
  }
}

export class LMKeyFieldDrillAroundFormEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  fieldId: string;

  constructor(event: Event, fieldId: string) {
    this.event = event;
    this.eventType = 'KeyFieldDrillAroundEvent';
    this.fieldId = fieldId;
  }
}

export class LMRelatedFormEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  actionReference?: LMBusActionReference;
  actionSpec?: LMActionSpec;
  fieldId?: string;

  constructor(event: Event, actionReference?: LMBusActionReference, actionSpec?: LMActionSpec) {
    this.event = event;
    this.eventType = 'RelatedEvent';
    this.actionReference = actionReference;
    this.actionSpec = actionSpec;
  }
}

export class LMTranslateFormEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  actionReference?: LMBusActionReference;
  actionSpec?: LMActionSpec;
  detName: string;
  busClass: string;

  constructor(event: Event, busClass: string, detName: string, actionReference?: LMBusActionReference, actionSpec?: LMActionSpec) {
    this.event = event;
    this.eventType = 'TranslateEvent';
    this.actionReference = actionReference;
    this.actionSpec = actionSpec;
    this.detName = detName;
    this.busClass = busClass;
  }
}

export class LMGenAIEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  actionReference?: LMBusActionReference;
  actionSpec?: LMActionSpec;
  fieldLabel: string;
  busClass: string;
  fieldId: string;

  constructor(event: Event, busClass: string, fieldLabel: string, actionReference?: LMBusActionReference, fieldId?: string) {
    this.event = event;
    this.eventType = 'GenAIEvent';
    this.actionReference = actionReference;
    this.fieldLabel = fieldLabel;
    this.busClass = busClass;
    this.fieldId = fieldId;
  }
}

export class LMDefineEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  fieldId: string;
  dataView: LMDataView;

  constructor(event: Event, fieldId: string, dataView: LMDataView) {
    this.event = event;
    this.eventType = 'DefineEvent';
    this.fieldId = fieldId;
    this.dataView = dataView;
  }
}

export class LMSubFormEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  fieldId: string;
  busFormSpec: LMBusFormSpec;
  title: LMMessageRule;

  constructor(event: Event, fieldId: string, title: LMMessageRule, busFormSpec: LMBusFormSpec) {
    this.event = event;
    this.eventType = 'OpenSubFormEvent';
    this.fieldId = fieldId;
    this.title = title;
    this.busFormSpec = busFormSpec;
  }
}

export class LMOpenLookupEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  fieldId: string;

  constructor(event: Event, fieldId: string) {
    this.event = event;
    this.eventType = 'OpenLookupEvent';
    this.fieldId = fieldId;
  }
}

export class LMCubeSearchOpenLookupEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  field: LMLookupComponent;

  constructor(event: Event, field: LMLookupComponent) {
    this.event = event;
    this.eventType = 'OpenCubeSearchLookupEvent';
    this.field = field;
  }
}

export class LMPreviewEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  query: string;
  busClass: string;
  fieldId: string;

  constructor(event: Event, query: string, busClass: string, fieldId: string) {
    this.event = event;
    this.eventType = 'PreviewEvent';
    this.query = query;
    this.busClass = busClass;
    this.fieldId = fieldId;
  }
}

export class LMQueryValidateEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  query: string;
  busClass: string;
  fieldId: string;
  computeType: string;
  isBusinessClassCompute: boolean;

  constructor(event: Event, query: string, busClass: string, fieldId: string, isBusinessClassCompute = false, computeType = '') {
    this.event = event;
    this.eventType = 'QueryValidateEvent';
    this.query = query;
    this.busClass = busClass;
    this.fieldId = fieldId;
    this.isBusinessClassCompute = isBusinessClassCompute;
    this.computeType = computeType;
  }
}

export class LMCubeRelationLPLValidationEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  query: string;
  busClass: string;
  fieldId: string;

  constructor(event: Event, query: string, busClass: string, fieldId: string) {
    this.event = event;
    this.eventType = 'CubeRelationLPLValidationEvent';
    this.query = query;
    this.busClass = busClass;
    this.fieldId = fieldId;
  }
}

export class LMInvalidLPLEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  hasLPLValidationError: boolean;

  constructor(event: Event, hasLPLValidationError: boolean) {
    this.event = event;
    this.eventType = 'InvalidLPLEvent';
    this.hasLPLValidationError = hasLPLValidationError;
  }
}

export class LMMemberFieldEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, eventType: LMFieldEventType) {
    this.event = event;
    this.eventType = eventType;
  }
}

export class LMFilterOperatorEvent implements LMFieldEvent {
  event: Event;
  eventType: LMFieldEventType;
  fieldId: string;
  operator: string;

  constructor(event: Event, operator: string, fieldId: string) {
    this.event = event;
    this.eventType = 'FilterOperatorEvent';
    this.operator = operator;
    this.fieldId = fieldId;
  }
}

interface LMFieldMessage {
  error: LMMessage;
  alert: LMMessageAlert;
}

interface LMMessage {
  type: SohoAlertType;
  message: string;
}

interface LMMessageAlert extends LMMessage {
  icon?: string;
}

interface LMFieldHistoryResponse {
  action?: string;
  actionComment?: string;
  actionReason?: string;
  actorId?: string;
  actorName?: string;
  appliedStamp?: string;
  auditField?: {
    decSize?: number;
    precision?: number;
    size?: number;
    type?: string;
  };
  auditValue?: string;
  authenticatedActorId?: string;
  authenticatedActorName?: string;
  effectiveDate?: string;
}

enum LMFormTypeEnum {
  SEARCH_FORM = 0
}

@Directive()
export class LMFieldBaseComponent extends LMBaseComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() layoutObject: LMFormLayoutField;
  @Input() mutexSpecField: LMSpecField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() placeHolder: any;
  @Input() layoutInfo: LMFormLayoutInfo;
  @Input() lmColumnLayoutInfo: LMColumnLayoutInfo;
  @Input() lmParagraphLayoutInfo: LMParagraphLayoutInfo;
  @Input() parentCompoundFieldDisplayOnly: boolean;
  @ViewChild(SohoAlertDirective) sohoAlert: SohoAlertDirective;

  @ViewChildren(SohoTrackDirtyDirective) trackDirtyChildren: QueryList<SohoTrackDirtyDirective>;
  @ViewChild(SohoBusyIndicatorDirective) _busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('Input', { read: ElementRef }) inputElement: ElementRef;
  @ViewChild(SohoTooltipDirective) tooltip: SohoTooltipDirective;
  @ViewChild('FieldOptionsDiv') fieldOptionsDiv: ElementRef;
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @Output() errorOnMemberField: EventEmitter<string> = new EventEmitter<string>();
  @HostBinding('class.lm-number-label-field') isNumberLabel = false;

  @HostBinding('class.lm-is-indent') isIndent = false;
  @HostListener('change')
  onChange() {
    // this.lmFieldServiceAdapter.lmLoggerService.log(['lm-field-base onChange()', this.bindId]);
    // checking this.dirty here does disallow more than one initial value rule call when
    // the form is initialized and after form is saved
    if (this.isDirty && this.lmFieldServiceAdapter.lmFormService.isFiringInitialValueRule === false) {
      this.hasFieldChanged = true;
    }

    if (this.fieldMessage.alert || this.fieldMessage.error) {
      this.resetAlert();
    }

    // hide label on a compound field when a value is entered, show the label if the value is cleared
    this.processShowLabel();

    if (this.layoutType === LMLayoutType.CHECKBOX || this.layoutType === LMLayoutType.RADIO) {
      this.handleOnFieldChanged();
    }
  }

  @HostBinding('class.is-compact-form-layout') useCompactFormLayout: boolean;

  @HostBinding('class.lm-display-as-text') displayAsText = false;

  @HostBinding('class.lm-display-as-tag') displayAsTag = false;

  @HostBinding('class.lm-hide-filter-type-dropdown') hideFilterTypeDropdown = false;

  @HostListener('click', [ '$event' ])
  onClick(event) {
    if (this.tooltip) {
      this.tooltip.hide();
    }

    if (this.isCompoundFieldMember) {
      this.onFocus(event);
    }

    //   if (event.ctrlKey && event.shiftKey) {
    //     event.stopPropagation();
    //     this.displayComponentInfo(event);
    //   }

    super.onClick(event);
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  onFieldFocus(event: FocusEvent) {
    // todo: need to find another way, commenting this out until then
    //  LMCLIENT-39830 Backout change for LMCLIENT-34216 Ensure custom controls are keyboard accessible.
    //  Just clicking on a field causes it to move to the center first. When attempting to open a drop down,
    //  open a data chooser, etc.. causes it to move to the center. Then the user has to click on the dropdown,
    //  date chooser, again to get it to come up.
    // (event.target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    this.displayFieldOptions = true;

    //soho dropdown doesnt trigger the onFocus function upon focus, so triggering it here on the focusin event.
    if (this.layoutType === LMLayoutType.DROPDOWN && this.isCompoundFieldMember) {
      this.onFocus(event);
    }

    if (this.tabPressed) {
      if (!this.tabbedForward) {
        // for fields with inline buttons, shift tabbing from the field options should skip the inline button
        // and should focus to the input element
        const elementToFocus: Element = event?.target as Element;
        const elementLosingFocus: Element = event?.relatedTarget as Element;
        if (this.layoutType === LMLayoutType.LOOKUP
          || this.layoutType === LMLayoutType.DATE
          || this.layoutType === LMLayoutType.TIME) {
          if (elementToFocus.classList.contains('btn-icon')
            && elementToFocus.classList.contains('trigger')
            && elementLosingFocus.classList.contains('field-options-button')) {
            this.getInputElement().focus();
          }
        }
      }
      this.tabPressed = false;
    }
  }

  onFieldBlur(event: FocusEvent) {
    // soho dropdown doesnt trigger the onBlur function upon blur, so triggering it here on the focusout event.
    if (this.layoutType === LMLayoutType.DROPDOWN && this.isCompoundFieldMember) {
      this.onBlur(event);
    }

    const elementLosingFocus: Element = event?.target as Element;
    const elementToFocus: Element = event?.relatedTarget as Element;

    // skip if delete attachment is clicked to avoid NG0100 error
    if (!elementToFocus && elementLosingFocus?.closest('lm-fileupload')) {
      return;
    }

    // if the focus is now on a different field or element, hide the field options.
    if (elementLosingFocus?.closest('lm-field') !== elementToFocus?.closest('lm-field')) {
      this.displayFieldOptions = false;
      if (this.tabPressed) {
        if (!this.tabbedForward) {
          if ((elementToFocus?.closest('lm-date')
            || elementToFocus?.closest('lm-time')
            || elementToFocus?.closest('lm-lookup'))) {
            //when shift tabbing from a field to another field with inline button, it should skip the inline button
            //and should focus to the input element.
            if (elementToFocus?.closest('lm-field') !== elementLosingFocus?.closest('lm-field')) {
              elementToFocus?.closest('lm-field')?.querySelector('input')?.focus();
            }
          }
        }
        this.tabPressed = false;
      }
    }
  }

  onTagFocus() {
    this.isFieldTagFocused = true;
  }

  onTagBlur() {
    this.isFieldTagFocused = false;
  }

  @HostListener('contextMenu', [ '$event' ])
  onContextMenu(event) {
    if (event.shiftKey && this.showContextMenu()) {
      event.preventDefault();
      this.openFieldOptions(event);
    }
  }

  @HostListener('keydown', [ '$event' ])
  onKeyDown(event: KeyboardEvent) {
    this.tabPressed = event.keyCode === 9;
    this.tabbedForward = this.tabPressed && !event.shiftKey;
    if (this.lmFieldServiceAdapter.lmKeyBoardShortcutService.onKeyDown(event) === LMKeyType.FIELDOPTION && this.showContextMenu()) {
      this.openFieldOptions(event);
    }
  }

  @HostListener('blur', [ '$event' ])
  onBlurEvent(event) {
    if (this.isCompoundFieldMember) {
      this.onBlur(event);
    }
  }

  public field: any;
  public label: any;
  public id: string;
  public fieldAutomationId: string;
  public fieldOptionsId: string;
  public scanOptionsId: string;
  public bindId: string;
  public modelField: LMModelField;
  public labelResourceValue: string = null;
  public insertContextMenu = false;
  public fieldClassesString: string;
  public isReadOnly = false;
  public isRequired = false;
  public onSearchForm = false;
  public tooltipId: string;
  public disabledMemberField = false;
  public triggerValue = false;
  public displayLabelAsValue = false;
  public showLabel = true;
  public compoundFieldInError = false;
  public serverSideError = false;
  public personalize: boolean;
  public onPageContextForm = false;
  public testVersion1: boolean;
  public fieldNameForOptionsMenu: string;
  public fieldOptionsLabel: string;
  public mouseOverTextContents: string;

  protected layoutObjectBase: LMFormLayoutField;
  protected specFieldsBase: LMSpecFields;
  protected modelBase: LMModel;
  protected layoutType: LMLayoutType;
  public isDirty = false;
  public size: number;
  public type: string;
  public maxLength: number;
  public specField: LMSpecField;
  protected _fieldClasses = '';
  protected _fieldClassArray: Array<string> = [];
  protected doHandleOnFieldChangedForDropdown = false;

  protected hasAlert = false;
  private runResetOriginalValue = false;
  private runResetOriginalValueCounter = 0;
  private runResetOriginalValueInitial = false;
  protected checkApplyState = false;
  protected runApplyState = false;
  private runAfterInitialValueRule = false;
  private showCompoundMemberFieldDirty = false;
  public dirtyCompoundField = false;

  // displayOnly property is also set when displayAsText is true; displayAsText will over-ride
  public displayOnly = false;

  protected timeStampDirective: LMTimeStampDirective;

  private tagStyleArray: LMTagStyle[];
  public tagStyle: LMTagStyle;

  private _isRequired = false;
  private _isConfigRequired = false;
  private isReadOnlyForm = false;
  private selectedText = { start: 0, end: 0 };

  private fieldMessage: LMFieldMessage = {} as LMFieldMessage;
  // alerts are in order of processing the alert state
  private alerts: Array<string> = [ 'redAlert', 'yellowAlert', 'greenAlert', 'blueAlert', 'iconAlert' ];

  protected subscriptionArray: Array<ISubscription> = [];
  private fireInitialKey: string = null;
  private fieldInlineError: string = null;
  protected hasFieldChanged = false;      // field changed while it had focus

  // soho-mask options for lm-text and lm-lookup
  public hasMask = false;
  public maskOptions: SohoMaskOptions = null;
  public searchFieldMaskOptions: SohoMaskOptions = null;
  public transformOptions: LMTransformOptions;

  // lm-displayupper directive for lm-text, lm-lookup, and lm-textarea
  public isUpperCase = false;

  public isFormText = false;
  public showField = true;
  public isCompoundFieldMember = false;
  public isPreviewCompoundMember = false;
  public dataEntryMode = false;
  private _parentCompoundFieldDisplayOnly: boolean;
  public tabIndexForFieldOptions = -1;
  public fieldIdAttributes: SohoAttributesArray = [];
  public isInAccessibilityMode: boolean;
  public displayFieldOptions: boolean;
  public fieldContextMenuOpen: boolean;
  protected tabPressed = false;
  protected tabbedForward = false;
  public instanceId: string;

  public isFieldTagFocused = false;

  public alphaFilterSettings: Array<SohoFieldFilterOption> = [
    { value: 'contains', text: Soho.Locale.translate('Contains'), icon: 'filter-contains' },
    { value: 'does-not-contain', text: Soho.Locale.translate('DoesNotContain'), icon: 'filter-does-not-contain' },
    { value: 'equals', text: Soho.Locale.translate('Equals'), icon: 'filter-equals' },
    { value: 'does-not-equal', text: Soho.Locale.translate('DoesNotEqual'), icon: 'filter-does-not-equal' },
    { value: 'is-empty', text: Soho.Locale.translate('IsEmpty'), icon: 'filter-is-empty' },
    { value: 'is-not-empty', text: Soho.Locale.translate('IsNotEmpty'), icon: 'filter-is-not-empty' },
    { value: 'end-with', text: Soho.Locale.translate('EndsWith'), icon: 'filter-end-with' },
    { value: 'does-not-end-with', text: Soho.Locale.translate('DoesNotEndWith'), icon: 'filter-does-not-end-with' },
    { value: 'start-with', text: Soho.Locale.translate('StartsWith'), icon: 'filter-start-with' },
    { value: 'does-not-start-with', text:  Soho.Locale.translate('DoesNotStartWith'), icon:  'filter-does-not-start-with' }
  ];

  public numericFilterSettings: Array<SohoFieldFilterOption> = [
    { value: 'equals', text: Soho.Locale.translate('Equals'), icon: 'filter-equals' },
    { value: 'does-not-equal', text: Soho.Locale.translate('DoesNotEqual'), icon: 'filter-does-not-equal' },
    { value: 'is-empty', text: Soho.Locale.translate('IsEmpty'), icon: 'filter-is-empty' },
    { value: 'is-not-empty', text: Soho.Locale.translate('IsNotEmpty'), icon: 'filter-is-not-empty' },
    { value: 'less-than', text: Soho.Locale.translate('LessThan'), icon: 'filter-less-than' },
    { value: 'less-equals', text: Soho.Locale.translate('LessOrEquals'), icon: 'filter-less-equals' },
    { value: 'greater-than', text: Soho.Locale.translate('GreaterThan'), icon: 'filter-greater-than' },
    { value: 'greater-equals', text: Soho.Locale.translate('GreaterOrEquals'), icon: 'filter-greater-equals' }
  ];

  public dateFilterSettings: Array<SohoFieldFilterOption> = [
    { value: 'equals', text: Soho.Locale.translate('Equals'), icon: 'filter-equals' },
    { value: 'does-not-equal', text: Soho.Locale.translate('DoesNotEqual'), icon: 'filter-does-not-equal' },
    { value: 'is-empty', text: Soho.Locale.translate('IsEmpty'), icon: 'filter-is-empty' },
    { value: 'is-not-empty', text: Soho.Locale.translate('IsNotEmpty'), icon: 'filter-is-not-empty' },
    { value: 'in-range', text: Soho.Locale.translate('InRange'), icon: 'filter-in-range' },
    { value: 'less-than', text: Soho.Locale.translate('LessThan'), icon: 'filter-less-than' },
    { value: 'less-equals', text: Soho.Locale.translate('LessOrEquals'), icon: 'filter-less-equals' },
    { value: 'greater-than', text: Soho.Locale.translate('GreaterThan'), icon: 'filter-greater-than' },
    { value: 'greater-equals', text: Soho.Locale.translate('GreaterOrEquals'), icon: 'filter-greater-equals' }
  ];

  public textSearchableSettings: Array<SohoFieldFilterOption> = [
    { value: 'start-with', text: Soho.Locale.translate('StartsWith'), icon: 'filter-start-with' }
  ];

  public cubeFilterSettings: Array<SohoFieldFilterOption> = [
    { value: 'equals', text: Soho.Locale.translate('Equals'), icon: 'filter-equals' },
  ];

  public formType: LMFormTypeEnum;
  public formDisplayType;

  public fieldHistoryInfo: Array<LMFieldHistoryResponse>;
  public popoverTitle: string;

  private onDestroy$ = new Subject<void>();
  private triggerMessageRule$ = new BehaviorSubject(false);
  public readOnly$ = new BehaviorSubject(false);
  public required$ = new BehaviorSubject(false);
  public specField$ = new BehaviorSubject(null);

  protected previousTimeZone: string;
  protected get isTimezoneField(): boolean {
    return !!(this.modelBase?.getField(this.bindId)?.dependentTimeZone.length);
  }

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter) {
    super(
      lmFieldServiceAdapter.sohoModalDialogService,
      lmFieldServiceAdapter.lmBusyIndicatorService,
      lmFieldServiceAdapter.lmComponentInfoService,
      lmFieldServiceAdapter.lmUniqueIdService,
      lmFieldServiceAdapter.lmAppComponentService,
    );

    if (lmFieldServiceAdapter.lmLoggerService) {
      this.instanceId = lmFieldServiceAdapter.lmLoggerService.instanceId;
    }
  }

  ngOnInit() {
    this.formDisplayType = LMFormTypeEnum;
    this.isCompoundFieldMember = (this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.isCompoundField);
    this.isPreviewCompoundMember = !!(this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.previewMode);
    this.showField = !this.isCompoundFieldMember;
    this.tabIndexForFieldOptions = this.isCompoundFieldMember ? 0 : -1;
    this.dataEntryMode = !!this.model?.formSpec?.dataEntryMode;
    this.useCompactFormLayout = this.lmFieldServiceAdapter.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;
    this.isInAccessibilityMode = this.lmFieldServiceAdapter.lmGlobalPersonalizationService.getAccessibilityMode();
  }

  ngAfterViewInit() {
    this.processAlerts();

    if (this.inputElement) {

      //When accessibility mode is disabled, filter field dropdowns are not accessible.
      if (this.formType === this.formDisplayType.SEARCH_FORM) {
        const filterDropdown = this.inputElement.nativeElement?.parentElement?.querySelector('select.field-filter-dropdown + div.dropdown-wrapper > div.dropdown');
        if (filterDropdown) {
          if (!this.isInAccessibilityMode) {
            filterDropdown.setAttribute('tabindex', '-1');
          }
          filterDropdown.addEventListener('focusout', (event: FocusEvent) => this.onFieldBlur(event));
        }
      }
      this.lmFieldServiceAdapter.renderer2.setAttribute(this.inputElement.nativeElement, 'autocomplete', 'new-user-input');
    }

    if (this.lmFieldServiceAdapter.lmLoggerService.cskLog === 2) {
      this.lmFieldServiceAdapter.lmLoggerService.timeEnd(`Total-Field-${this.instanceId}`);
    }
  }

  ngAfterViewChecked() {
    // LMCLIENT-29895 discovered that we were getting an expression changed error when applying model from the
    // compound form modal being dismissed. Making sure we only change the new flag one per bindId instance, and
    // in the next tick so we don't get the expression changed error. This is currently only for a time, date, lookup
    // or text member field
    if (this.showCompoundMemberFieldDirty && this.isCompoundFieldMember) {
      this.showCompoundMemberFieldDirty = false;
      if (this.dirtyCompoundField !== this.isDirty) {
        this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.dirtyCompoundField = this.isDirty;
          }, 1);
        });
      }
    }

    if (this.layoutObject?.exportFilterCriteria) {
      this.modelField.exportFilterCriteria = this.layoutObject.exportFilterCriteria;
    }

    /**
     * If disableTranslationFiltering is set on the BusFormField then propagate
     * that information to the modelField. LMFormContentSearchComponent can then
     * use that to set the LMFilterFieldCriteria.disableTranslationFiltering flag.
     */
    if (this.layoutObject?.disableTranslationFiltering) {
      this.modelField.disableTranslationFiltering = true;
    }

    // LMCLIENT-23980: cannot call resetDirty() on this tick as the underlying control has not been
    // updated by ngModel, so wait one tick and then call resetDirty()
    if (this.runResetOriginalValue && this.runResetOriginalValueCounter++ === 1) {
      if (this.trackDirtyChildren) {
        // possible scenario: a field component is being initialized late, possibly due to visible when or a lazy loading
        // form and another instance of the field is on another part of the form and has been made dirty,
        // so apply the dirty state properly
        if (this.runResetOriginalValueInitial && this.modelField.evaluateIsDirty) {
          this.isDirty = this.model.evaluateAndApplyDirtyState(this.bindId);
        } else if (!this.isReadOnly && this.layoutType !== LMLayoutType.BINARY_DOCUMENT) {
          // radio buttons have a soho-trackdirty directive for each radio and each one needs to be called to be reset
          this.trackDirtyChildren.forEach(trackDirtyChild => {
            trackDirtyChild.resetDirty();
          });
        }
      }
      this.runResetOriginalValue = false;
      this.runResetOriginalValueInitial = false;
      this.runResetOriginalValueCounter = 0;
    }

    // LMCLIENT-24650: if the modelChanged update has occurred (checkApplyState is true)
    // and the notification for applyState has been emitted (runApplyState is true), then
    // indicate to the sohoxi controls to evaluate the dirty condition of the field
    if (this.checkApplyState && this.runApplyState) {
      if (this.trackDirtyChildren && !this.isReadOnly) {

        let index = 0;

        // SPECIAL CASE: need to iterate the states array to find the index for the value and only call changeDirty
        // for that trackDirty child
        if (this.layoutType === LMLayoutType.RADIO) {
          let found = false;
          let hasBlank = false;
          const value = this.modelField.value;
          const states = this.modelField.states;
          const len = states.length;

          // using for loop so can break when found
          for (index = 0; index < len; index++) {
            // radios do not display the blank value, but need to subtract one from the index if one exists
            // using same evaluation as ngIf in template
            if (!hasBlank && states[ index ].label === ' ') {
              hasBlank = true;
            }

            // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
            // eslint-disable-next-line eqeqeq
            if (value == states[ index ].value ) {
              found = true;
              break;
            }
          }

          // if found, adjust the index and call changeDirty on that trackDirty child only
          if (found) {
            if (hasBlank) {
              index -= 1;
            }

            // safety
            if (index < 0) {
              index = 0;
            }
          }
        }

        // the value has changed from a when value changed/initial value rules, so reset any alerts
        if (this.fieldMessage.alert || this.fieldMessage.error) {
          this.resetAlert(false);
        }

        this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.trackDirtyChildren.forEach(( trackDirtyChild: SohoTrackDirtyDirective, childIndex ) => {
              if (index === childIndex) {
                trackDirtyChild.changeDirty();
                this.showCompoundMemberFieldDirty = true;
              }
            });
          }, 1);
        });

      } else if (!this.trackDirtyChildren || this.isReadOnly) {
        // need to process readOnly and non-input fields (displayAsText, etc)
        this.isDirty = this.model.evaluateAndApplyDirtyState(this.bindId);
      }

      this.checkApplyState = false;
      this.runApplyState = false;
    }

    // LMCLIENT-25444: read only fields populated by an initial value rule need to be evaluated and maintained
    // so the model state is correct
    if (this.checkApplyState && this.runAfterInitialValueRule) {
      this.isDirty = this.model.evaluateAndApplyDirtyState(this.bindId);

      this.checkApplyState = false;
      this.runAfterInitialValueRule = false;
    }

    // LMCLIENT-25562: needed to wait to handleOnFieldChanged if dropdown was dependent on visible when
    // conditions. Caused an http failure in IE
    if (this.doHandleOnFieldChangedForDropdown) {
      this.doHandleOnFieldChangedForDropdown = false;
      this.handleOnFieldChanged();
    }
  }

  ngOnDestroy() {
    // clean up singleton field subscriptions
    if (this.bindId) {
      this.lmFieldServiceAdapter.lmFieldSubscriptionService.remove(this.fireInitialKey);
      this.lmFieldServiceAdapter.lmFieldSubscriptionService.remove(this.fieldInlineError);
    }

    // clean up all other subscriptions
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.subscriptionArray = [];

    if (this.onDestroy$) {
      // todo why is onDestroy$ undefined in ng8 JIT compiler
      this.onDestroy$.next();
      this.onDestroy$.unsubscribe();
      this.onDestroy$ = null;
    }

    if (this.fieldEvent) {
      // todo why is fieldEvent undefined in ng8 JIT compiler
      this.fieldEvent.unsubscribe();
      this.fieldEvent = null;
    }

    if (this.errorOnMemberField) {
      // todo why is errorOnMemberField undefined in ng8 JIT compiler
      this.errorOnMemberField.unsubscribe();
      this.errorOnMemberField = null;
    }

    if (this.triggerMessageRule$) {
      this.triggerMessageRule$.unsubscribe();
      this.triggerMessageRule$ = null;
    }

    if (this.specField$) {
      this.specField$.unsubscribe();
      this.specField$ = null;
    }
  }

  public getSpec() {
    return undefined;
  }

  public isRouteReuseActive(): boolean {
    return false;  // nothing currently implemented
  }

  public setRouteReuseActive(active: boolean): void {
    // noop att his point
  }

  // getters and setters
  protected set busyIndicator(value: boolean) {
    if (this._busyIndicator) {
      this._busyIndicator.activated = value;
    }
  }

  get configRequiredValidator(): string {
    // used to set the sohoxi required validator on the data-validate attribute on a control
    // return null means attribute is not set
    if (this._isConfigRequired) {
      return 'required';
    }
    return null;
  }

  protected get compactFormDialogSpec(): LMSpecField {
    if (this.field.type === AppConstants.BUTTON_TYPE_SHOWEDITOR) {
      const spec: LMSpecField = this.specFields[ this.field.fieldId ];

      if (spec && spec.compactFormat) {
        return spec;
      }
    }

    return null;
  }

  public get fieldClasses(): string {
    this._fieldClasses = this._fieldClassArray.join(' ');
    return this._fieldClasses;
  }

  protected get fieldClassArray(): Array<string> {
    return this._fieldClassArray;
  }

  protected set fieldClassArray(value: Array<string>) {
    this._fieldClassArray = value;
  }

  protected get isContextForm(): boolean {
    if (this.layoutInfo) {
      return this.layoutInfo.isContextForm;
    }
    return false;
  }

  protected get isSubForm(): boolean {
    return (this.field.type === AppConstants.BUTTON_TYPE_SUBFORM);
  }

  public get stateAsText(): string {
    return this.lmFieldServiceAdapter.lmFormatUtilService.stateLabelToText(this.modelField.states, this.modelField.value);
  }

  private get validityParams() {
    if (this.lmFieldServiceAdapter.lmConditionUtilService) {

      let displayAsRequired = false;

      if (this.field.displayAsRequired) {
        displayAsRequired = this.lmFieldServiceAdapter.lmConditionUtilService.isValid(this.field.displayAsRequired, this.model);
      }

      const validityParams = {
        isFormMutable: this.modelBase.isFormMutable,
        isMutable: this.modelField.mutable,
        isRequired: this.modelField.required,  // tri-state; null, false, true
        displayAsRequired
      };

      if (this.field.validity && this.specFieldsBase && validityParams.isMutable && validityParams.isFormMutable) {
        validityParams.isMutable = this.lmFieldServiceAdapter.lmConditionUtilService.isValid(this.field.validity, this.modelBase);
      }

      return validityParams;
    }

    return null;
  }

  private get isDisplayZero(): boolean {
    if (this.specField
      && (this.specField.type === AppConstants.NUMERIC || this.specField.type === AppConstants.BCD || this.specField.type === AppConstants.PERCENT)
      && (this.layoutType === LMLayoutType.LOOKUP || this.layoutType === LMLayoutType.TEXT)) {

      // honor the directive first
      if (this.field.hasOwnProperty('displayZero')) {
        return true;
      }

      // always display zeros for BCD and Percent displayAsText or displayAsTag
      if ((this.specField.type === AppConstants.BCD || this.specField.type === AppConstants.PERCENT) && !this.isCompoundFieldMember && (this.displayAsText || this.displayAsTag)) {
        return true;
      }
    }

    return false;
  }

  public get triggerValueChange(): BehaviorSubject<boolean> {
    return this.triggerMessageRule$;
  }

  // eslint-disable-next-line no-unused-vars
  onAfterResetDirty(event: SohoTrackDirtyEvent) {
    // only reset dirty to pristine on change if already dirty
    if (this.isDirty) {
      this.isDirty = false;
      this.model.setDirty(this.bindId, this.isDirty);
    }
  }

  // eslint-disable-next-line no-unused-vars
  onDirty(event?: SohoTrackDirtyEvent) {
    // only set dirty if not already dirty
    if (!this.isDirty) {
      this.isDirty = true;
      this.model.setDirty(this.bindId, this.isDirty);
    }
  }

  // eslint-disable-next-line no-unused-vars
  onPristine(event?: SohoTrackDirtyEvent) {
    // only set pristine if already dirty
    if (this.isDirty) {
      this.isDirty = false;
      this.model.setDirty(this.bindId, this.isDirty);
    }
  }

  onBlur(event) {

    if (this.hasFieldChanged) {
      // field changed while it had focus
      this.handleOnFieldChanged();
    }

    if (!(this.layoutType === LMLayoutType.CHECKBOX || this.layoutType === LMLayoutType.DATE
        || this.layoutType === LMLayoutType.LOOKUP || this.layoutType === LMLayoutType.DROPDOWN
        || this.layoutType === LMLayoutType.RADIO || this.layoutType === LMLayoutType.TIME
        || this.layoutType === LMLayoutType.CHECKBOXGROUP)
      && event.currentTarget) {
      this.selectedText = this.getInputSelection(event.currentTarget);
    }

    if (this.isCompoundFieldMember) {
      if (this.parentCompoundFieldDisplayOnly === true || this.disabledMemberField || this.displayAsText || this.isPreviewCompoundMember) {
        return;
      }

      if (!this.showField) {
        return;
      }

      this.showField = false;

      // Need to manually activate lookup fields and dropdown
      // Removes blue outline when tabbing out or clicking away from dropdown
      if ((event && this.layoutType === LMLayoutType.LOOKUP || this.layoutType === LMLayoutType.DROPDOWN
        || this.layoutType === LMLayoutType.DATE || this.layoutType === LMLayoutType.CHECKBOX
        || this.layoutType === LMLayoutType.TIME || this.layoutType === LMLayoutType.RADIO)) {
        this.toggleActiveClass(event.target);
      }
      this.fieldEvent.emit(new LMMemberFieldEvent(event, 'MemberBlurEvent'));
    }
  }

  // input event occurs between keypress and keyup events
  // eslint-disable-next-line no-unused-vars
  onInput(event) {
    // this.lmFieldServiceAdapter.lmLoggerService.log(['lm-field-base onInput()', this.bindId]);

    // evaluate if the control is dirty or pristine
    if (this.trackDirtyChildren && !this.isReadOnly) {
      this.trackDirtyChildren.forEach(dirtyChild => dirtyChild.changeDirty());
    }

    this.hasFieldChanged = true;
  }

  onFocus(event) {
    if ((this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.isCompoundField && !this.isPreviewCompoundMember)) {
      if (this.parentCompoundFieldDisplayOnly === true || this.disabledMemberField || this.displayAsText) {
        return;
      }

      if (this.showField) {
        return;
      }

      this.showField = true;

      this.fieldEvent.emit(new LMMemberFieldEvent(event, 'MemberFocusEvent'));

      if (this.layoutType === LMLayoutType.LOOKUP || this.layoutType === LMLayoutType.DROPDOWN
        || this.layoutType === LMLayoutType.DATE || this.layoutType === LMLayoutType.CHECKBOX
        || this.layoutType === LMLayoutType.TIME || this.layoutType === LMLayoutType.RADIO) {
        if (event) {
          this.toggleActiveClass(event.target);
        } else {
          this.toggleActiveClass(this.getInputElement());
        }
      }

      // SetTimeout needed to ensure the input s fully displayed before focus
      // Run outside to prevent setTimeout from firing change detection

      this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
        // Need to manually focus into compound member field inputs. This is so the user doesn't need to tab
        // twice to edit a member field
        setTimeout(() => {
          const inputElement: HTMLElement = this.getInputElement() as HTMLElement;

          // need to focus on the dropdown wrapper for the dropdown component.
          if (this.layoutType === LMLayoutType.DROPDOWN) {
            if ((event?.target as HTMLElement)?.closest('p')) {
              ((event?.target as HTMLElement)?.closest('p')
                ?.previousElementSibling
                ?.closest('div.dropdown-wrapper')
                ?.querySelector('div.dropdown') as HTMLElement).focus();
            }
          } else {
            if (inputElement) {
              inputElement.focus();
            }
          }

        }, 10);
      });
    }
  }

  /**
   * Compact group field.
   * Need to toggle active class for dropdowns and lookups
   * @param element
   * TODO: Might be a way to not have to do this?
   */
  toggleActiveClass(element) {
    if (!element) {
      return;
    }
    // Parent
    const gp = element.closest('lm-field');
    const p = element.closest('.field');
    const activeClass = 'is-active';

    if (p.classList.contains(activeClass)) {
      p.classList.remove(activeClass);

    } else if (!p.classList.contains(activeClass)) {
      p.classList.add(activeClass);
    }

    // Grand parent
    if (gp.classList.contains(activeClass)) {
      gp.classList.remove(activeClass);

    } else if (!gp.classList.contains(activeClass)) {
      gp.classList.add(activeClass);
    }
  }

  onFieldEvent(event) {
    this.fieldEvent.emit(event);
  }

  /**
   * Triggers a focus event if field component is clicked
   * @param event
   */
  onCompoundFieldClickEvent(event) {
    if (this.layoutType === LMLayoutType.CHECKBOX) {
      return;
    }

    if (this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.isCompoundField) {
      this.onFocus(event);
    }
  }

  // todo type me!
  onContextMenuEvent(event: any) {
    if (event.args) {
      const menuElement = event.args[ 0 ];
      const menuId = menuElement.getAttribute('id');

      if (menuId === AppConstants.FORM_CONTEXTMENUACTION_TEXTVARIABLES) {
        const dataActionReference = event.args[ 0 ].dataset.actionReference;
        const textRepObj = JSON.parse(dataActionReference);
        const repValue = textRepObj.id;
        const inputElement = this.getInputElement();

        if (inputElement) {
          inputElement.focus();
          inputElement.selectionStart = this.selectedText.start;
          inputElement.selectionEnd = this.selectedText.end;

          if (this.lmFieldServiceAdapter.lmBrowserUtilService.isIE11) {
            // this works for a text or textarea field
            // rich text editor: insertion works ok, but selected (highlighted) text is not removed
            document.execCommand('paste', false, '{' + repValue + '}');

          } else {
            document.execCommand('insertText', false, '{' + repValue + '}');
          }

        } else {
          // safety condition: display a popover message indicate to place the cursor in the field and re-try
          let message;
          if (this.labelResourceValue) {
            const fieldLabel = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
            message = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldTextVariableInsertionMessage'), [ fieldLabel ]);

          } else {
            message = Soho.Locale.translate('TextVariableInsertionMessage');
          }

          // use the context menu item as the popover title
          this.tooltip.title = this.popoverTitle = Soho.Locale.translate('TextVariableReplacement');
          this.tooltip.content = this.buildTextVariableInsertionMessage(message);
          this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
            setTimeout(() => (this.tooltip.ngOnChanges(), this.tooltip.show()), 1);
          });
        }

      } else if (menuId === 'DefineEvent') {
        this.fieldEvent.emit(new LMDefineEvent(event, this.bindId, this.model.getData()));

      } else if (menuId === 'TranslateEvent') {
        const dataActionReference = event.args[ 0 ].dataset.actionReference;
        const actionReference = JSON.parse(dataActionReference) as LMBusActionReference;
        this.fieldEvent.emit(new LMTranslateFormEvent(event, this.model.getData().busClassName, this.specField.detName, actionReference));

      } else {
        if (menuId === 'CompactForm') {
          const exclude = this.specField.selectable && this.specField.compactFormat;
          this.fieldEvent.emit(new LMCompactFormEvent(event, this.bindId, this.specField.label, this.specField.layout, this.specFields, this.layoutObject, this.displayOnly, exclude));

        } else if (menuId === 'related') {
          const dataActionReference = event.args[0].dataset.actionReference;
          const actionReference = JSON.parse(dataActionReference) as LMBusActionReference;
          this.fieldEvent.emit(new LMRelatedFormEvent(event, actionReference));
        } else if (menuId === 'StandardDrillAroundButtonEvent') {
          this.fieldEvent.emit(new LMKeyFieldDrillAroundFormEvent(event, this.bindId));
        } else if ('ShowFieldHistoryEvent' === menuId) {
          this.lmFieldServiceAdapter.lmFieldOptionsService.getFieldHistory(this.bindId, this.model.getData(), this.model.formUrlInfo, this.model.formSpec)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
              (response: Array<LMFieldHistoryResponse>) => {
                this.fieldHistoryInfo = response;
                let tooltipTitle;
                if (this.labelResourceValue) {
                  const fieldLabel = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
                  tooltipTitle = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldNameHistory'), [ fieldLabel ]);
                } else {
                  tooltipTitle = Soho.Locale.translate('FieldHistory');
                }
                this.tooltip.title = this.popoverTitle = tooltipTitle;
                this.tooltip.content = this.buildFieldHistoryContent();
                this.tooltip.extraClass = 'fieldaudit';
                this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
                  setTimeout(() => (this.tooltip.ngOnChanges(), this.tooltip.show()), 1);
                });
              });

        } else if ('ShowPendingChangesEvent' === menuId) {
          this.lmFieldServiceAdapter.lmFieldOptionsService.getPendingChanges(this.bindId, this.model.getData(), this.model.formUrlInfo, this.model.formSpec)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
              (response: Array<LMFieldHistoryResponse>) => {
                this.fieldHistoryInfo = response;
                let tooltipTitle;
                if (this.labelResourceValue) {
                  const fieldLabel = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
                  tooltipTitle = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldPendingChanges'), [ fieldLabel ]);
                } else {
                  tooltipTitle = Soho.Locale.translate('PendingChanges');
                }
                this.tooltip.title = this.popoverTitle = tooltipTitle;
                this.tooltip.content = this.buildFieldHistoryContent();
                this.tooltip.extraClass = 'fieldaudit';
                this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
                  setTimeout(() => (this.tooltip.ngOnChanges(), this.tooltip.show()), 1);
                });
              });

        } else {
          this.fieldEvent.emit({ event, eventType: menuId });
        }
      }

    } else {
      this.fieldEvent.emit(event);
    }
  }

  onError(event: SohoInputValidateEvent) {
    // this.lmFieldServiceAdapter.lmLoggerService.log([this, 'onError', this.bindId, event.validation.message]);

    // TODO: this event can be triggered from Angular; needs investigation
    if (event && event.validation) {
      this.processFieldMessage(event.validation.message);
      this.model.setError(this.bindId, true, event.validation.message);
    }
  }

  // eslint-disable-next-line no-unused-vars
  onValid(event: SohoInputValidateEvent) {
    // this.lmFieldServiceAdapter.lmLoggerService.log([this, 'onValid', this.bindId, event.validation.message]);

    this.processFieldMessage('');
    this.model.setError(this.bindId, false);
  }

  onFiltered(event: SohoFieldFilteredEvent) {
    this.modelField.filterType = this.getLMOperator(event.filterOption.value);
    this.transformOptions.isRange = (this.modelField.filterType === 'inRange');
    this.fieldEvent.emit(new LMFilterOperatorEvent( new CustomEvent(''), this.getLMOperator(event.filterOption.value), this.bindId ));
  }

  public onBeforeScanContextMenuOpen = (response?: AjaxBeforeOpenResponseFunction, options?: any) => {
    let labelValue = '';
    if (this.labelResourceValue) {
      labelValue = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
    }

    this.buildScanContextMenu(response,
      options,
      this.field,
      this.specField,
      this.showCompactFormContextMenu(),
      labelValue,
      this.modelField,
      this.model
    );
  };

  public buildScanContextMenu(
    response: AjaxBeforeOpenResponseFunction,
    options: any,
    field: any,
    specField: any,
    showCompact: boolean,
    fieldLabel: string,
    modelField: LMModelField,
    model: LMModel
  ) {
    const contextActions = this.getScanContextMenuActions(fieldLabel);

    let initialResponse = '';

    // only show Lookup if the field is scannable and inside inforgo
    if (specField.definable && field.scannable) {
      const menuItem: LMToolbarButton = contextActions.scan;
      const id: string = menuItem.actionReference.id + 'Event';
      initialResponse +=
        `<li attr.disabled="${menuItem.state.disabled}">
          <a id="${id}" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
        </li>`;
    }

    // only show Lookup if the field is scannable and inside inforgo
    if (specField.definable && field.scannable) {
      const menuItem: LMToolbarButton = contextActions.select;
      const id: string = menuItem.actionReference.id + 'Event';
      initialResponse +=
        `<li attr.disabled="${menuItem.state.disabled}">
          <a id="${id}" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
        </li>`;
    }

    response(initialResponse);
  }

  private getScanContextMenuActions(fieldLabel: string): { [ key: string ]: LMToolbarButton } {
    return {
      scan: {
        actionReference: {
          id:    AppConstants.FORM_CONTEXTMENUACTION_SCAN,
          type:  'standard',
          label: Soho.Locale.translate('Scan')
        }, state:        { disabled: false, visible: true }
      },
      select: {
        actionReference: {
          id:    AppConstants.FORM_CONTEXTMENUACTION_SELECT,
          type:  'standard',
          label: Soho.Locale.translate('Select')
        }, state:        { disabled: false, visible: true }
      },
    };
  }

  public onBeforeContextMenuOpen = (response: AjaxBeforeOpenResponseFunction, options: any) => {
    let labelValue = '';
    if (this.labelResourceValue) {
      labelValue = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
    }

    this.lmFieldServiceAdapter.lmFieldOptionsToolbarService.buildContextMenu(response,
      options,
      this.field,
      this.specField,
      this.showCompactFormContextMenu(),
      labelValue,
      this.modelField,
      this.model
    );
  };

  // protected methods
  protected fireInitialValueRuleEvent(bindId: string) {
    this.fieldEvent.emit(
      new LMFieldValueChangedEvent(
        new CustomEvent('ValueChanged'), 'FireInitialValueRuleEvent', bindId));
  }

  protected fireEvaluateConditionsEvent(bindId: string) {
    // can be called from handldOnFieldChange for a field that is hidden on the form, so fieldEvent isn't defined. Need
    // to check that the emitter exists
    if (this.fieldEvent) {
      this.fieldEvent.emit(new LMFieldValueChangedEvent(
        new CustomEvent('ValueChanged'), 'EvaluateConditionsEvent', bindId));
    }
  }

  protected fireTemplateFieldChangedEvent(bindId: string) {
    //template dynamic child pattern needs to know when fields change
    if (this.fieldEvent) {
      this.fieldEvent.emit(new LMTemplateFieldValueChangedEvent(
          new CustomEvent('ValueChanged'), 'TemplateFieldChangedEvent', bindId));
    }
  }

  // TODO: TECHNICAL DEBT: consider resolving the need to pass arguments here; was required when we first started
  // with angular, but now is not necessary
  protected initialize(layoutObject: LMFormLayoutField, specFields: LMSpecFields, model: LMModel) {
    this.layoutObjectBase = layoutObject;
    this.specFieldsBase = specFields;
    this.modelBase = model;

    this.field = this.layoutObjectBase;
    this.bindId = (this.mutexSpecField)
      ? this.mutexSpecField.bindId
      : (this.field.hasOwnProperty('fieldId'))
        ? this.field.fieldId
        : this.field.bindId;

    if (this.lmFieldServiceAdapter.lmLoggerService.cskLog === 2) {
      this.lmFieldServiceAdapter.lmLoggerService.time({
        id: `Total-Field-${this.instanceId}`,
        description: 'TOTAL FIELD',
        functionName: 'initialize()',
        className: 'LMFieldBaseComponent',
        timingStart: 'LMFieldBaseComponent -> initialize()',
        timingEnd: 'LMFieldBaseComponent -> ngAfterViewInit()',
        fieldType: this.layoutObject.type,
        fieldId: this.uniqueId(this.bindId)
      });
    }

    this.testVersion1 = this.lmFieldServiceAdapter.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.id = this.uniqueId(this.bindId);
    // if (this.layoutObject?.automationId) {
    //   this.fieldAutomationId = this.uniqueFieldAutomationId(this.layoutObject.automationId);
    // } else {
    //   this.fieldAutomationId = this.uniqueFieldAutomationId(this.bindId);
    // }

    if(layoutObject.type && layoutObject.type === 'formtext' && layoutObject.automationId) {
      this.fieldAutomationId = this.uniqueFieldAutomationId(layoutObject.automationId);
    } else {
      this.fieldAutomationId = this.uniqueFieldAutomationId(this.bindId);
    }
    this.fieldIdAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.fieldAutomationId });

    if (!this.testVersion1) {
      this.fieldIdAttributes.push({ name: 'id', value: this.id });
    }

    // this.fieldIdAttributes = this.testVersion1 ? null : [ { name: 'id', value: this.id } ];

    this.fieldOptionsId = this.lmFieldServiceAdapter.lmUniqueIdService.uniqueId([ this.id, 'FieldOptions' ]);
    this.scanOptionsId = this.lmFieldServiceAdapter.lmUniqueIdService.uniqueId([ this.id, 'ScanOptions' ]);
    this.tooltipId = '#field-history-tooltip-' + this.id;

    this.specField = (this.bindId !== undefined && this.specFieldsBase.hasOwnProperty(this.bindId))
      ? this.specFieldsBase[ this.bindId ]
      : null;

    this.specField$.next(this.specField);

    if (this.mutexSpecField) {
      this.layoutType = this.lmFieldServiceAdapter.lmFieldLayoutService.specFieldToLayoutType(this.field, this.mutexSpecField);
    } else {
      this.layoutType = this.lmFieldServiceAdapter.lmFieldLayoutService.getLayoutType(this.field, this.specField, model, this.lmParagraphLayoutInfo);
    }

    // Hide filter conditions on search from for all layout types but data and time when config feature set
    if (this.model.isSearchForm && (this.layoutType !== LMLayoutType.DATE && this.layoutType !== LMLayoutType.TIME)) {
      if (this.lmFieldServiceAdapter.lmContextService.isConfigFeatureEnabled('HideSearchFieldOptions')) {
        this.hideFilterTypeDropdown = true;
      }
    }

    if (this.field.indent) {
      this.isIndent = this.field.indent;
    }

    if (this.field.label) {
      this.label = this.field.label;

      if (this.field.numberLabel) {
        this.isNumberLabel = this.field.numberLabel;
      }

      if (this.label.label) {
        this.labelResourceValue = this.label.label;
      } else {
        this.labelResourceValue = this.label.resourceValue;
      }
      if (this.labelResourceValue) {
        this.fieldNameForOptionsMenu = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields);
      }
    }
    if ((this.fieldNameForOptionsMenu?.trim() || this.field.foregroundText)) {
      this.fieldOptionsLabel = 'MoreFieldOptionsTextLabel';
    } else {
      this.fieldOptionsLabel = 'MoreFieldOptionsTextNoLabel';
    }

    if (this.specField) {
      this.isUpperCase = (this.specField.type === AppConstants.ALPHAUPPER);
      this.fieldClassArray.push(this.bindId);

      if (this.specField.type === AppConstants.TIMESTAMP) {
        if (this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayDateOnly(this.field, this.specField)) {
          this.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE;

        } else if (this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayTimeOnly(this.field, this.specField)) {
          this.timeStampDirective = LMTimeStampDirective.DISPLAY_TIME;

        } else if (this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayHoursAndMinutesOnly(this.field, this.specField)) {
          this.timeStampDirective = LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES;
        } else if (this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayDateAndHoursAndMinutes(this.field, this.specField)
        || this.lmFieldServiceAdapter.lmFieldLayoutService.hasLimitToMinuteIncrements(this.field, this.specField)) {
          this.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES;
        }
      } else if (this.specField.type === AppConstants.TIME) {
        if (this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayHoursAndMinutesOnly(this.field, this.specField)
        || this.lmFieldServiceAdapter.lmFieldLayoutService.hasLimitToMinuteIncrements(this.field, this.specField)) {
          this.timeStampDirective = LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES;
        }
      }
    }

    this.modelField = this.getModelField();

    if (this.layoutObject.timeZoneIs) {
      this.modelBase.getField(this.layoutObject.timeZoneIs).dependentTimeZone.push(this.bindId);
    }

    this.isReadOnlyForm = (this.layoutInfo && this.layoutInfo.isReadOnlyForm) || this.modelBase.isReadOnlyForm;

    if (this.isCompoundFieldMember && (this.lmParagraphLayoutInfo.displayAsText || this.lmParagraphLayoutInfo.displayOnly || this.isPreviewCompoundMember)) {
      // if compound field that is display as text, display only or the preview mode, behave like a read only form. We don't want to be editable
      this.isReadOnlyForm = true;
    }

    this.displayAsTag = this.field.displayAsTag || false;

    this.tagStyleArray = this.field.tagStyle || null;

    // this.displayAsText = this.model.isDrillAroundMode() || this.field.displayAsText || false;
    this.displayAsText = this.field.displayAsText || false;

    this.displayOnly = (this.field.displayOnly && !this.displayAsText) || false;

    this._isConfigRequired = this.layoutObjectBase.hasOwnProperty('cfgRequired');

    if (this.layoutInfo && !this.lmFieldServiceAdapter.lmThemeService.getCurrentThemeId()?.includes('light')) {
      this.personalize = this.lmFieldServiceAdapter.lmThemeService.isPersonalized(this.layoutInfo.isContextForm, this.model.formSpec.busFormSpec);
    }


    this.subscriptionArray.push(this.lmFieldServiceAdapter.lmThemeService.getThemeChangeSubject().subscribe((theme: string) => {
      if (theme.includes('light')) {
        this.personalize = false;
      } else if (this.layoutInfo) {
        this.personalize = this.lmFieldServiceAdapter.lmThemeService.isPersonalized(this.layoutInfo.isContextForm, this.model.formSpec.busFormSpec);
      }
    }));


    this._isRequired = (this.field.required && !this.displayAsText)
      || this._isConfigRequired
      || false;

    this.onSearchForm = this.model.isSearchForm;
    this.onPageContextForm = this.model.isPageContextForm;

    if (this.onSearchForm) {
      this.formType = LMFormTypeEnum.SEARCH_FORM;

      // only set the default alpha operator when it is a alpha/text type,
      // an doesn't have state fields
      if (this.specField &&
        (this.specField.type === AppConstants.ALPHA ||
        this.specField.type === AppConstants.ALPHAUPPER ||
        this.specField.type === AppConstants.ALPHALOWER ||
        this.specField.type === AppConstants.TEXT ||
        this.specField.type === AppConstants.TEXTAREA) &&
        !this.specField.hasUserDefinedStates &&
        !this.specField.stateValues) {
        this.modelField.filterType = this.modelField?.filterCriteria?.operator || this.modelField.alphaFieldDefaultType;
      }

      if (this.specField?.defaultFilterOperator) {
        this.modelField.filterType = this.modelField?.filterCriteria?.operator || this.specField.defaultFilterOperator;
      }
    }

    this.insertContextMenu = this.showContextMenu();

    // set isFormText to hide input element
    this.isFormText = (this.field.type === 'formtext');

    if (this.isReadOnlyForm) {
      this._isRequired = false;

      if (this.onPageContextForm || (this.layoutInfo && this.layoutInfo.isDashboardPanelForm)) {
        this.displayAsText = true;
        this.displayOnly = false;

      } else if (this.isCompoundFieldMember) {
        // compound field members should inherit their parent field's status
        if (this.lmParagraphLayoutInfo.displayAsText || this.isPreviewCompoundMember) {
          this.displayAsText = true;
          this.displayOnly = false;

        } else if (this.lmParagraphLayoutInfo.displayOnly) {
          this.disabledMemberField = true;
          this.displayOnly = true;

        }
      } else if (this.isFormText) {
        // allow form text to display correctly when read only
        // displayAsText and isFormText needs to be mutually exclusive
        this.displayAsText = false;

      } else {
        // generic read only form, set all fields to display only unless the field itself is marked as display as text
        this.displayOnly = true;

        if (this.field.displayAsText) {
          this.displayAsText = true;
          this.displayOnly = false;
        }
      }
    }

    if (this.bindId) {
      // LMFieldSubscriptionService is different than the subscriptionArray
      // In LMFieldSubscriptionService, there is only one entry / subscription for a key as we only want one notification
      // to be emitted and not multiple subscriptions for multiple instances of a bindId on a form

      // keys for LMFieldSubscriptionService
      this.fireInitialKey = `FireInitialValueRuleCompleted_${this.id}`;
      this.fieldInlineError = `InlineError_${this.bindId}`;
    }

    if (this.modelField.fireInitialValue) {
      this.subscribeToFireInitialValueRuleCompleted();
    }

    if (!this.onSearchForm) {
      this.subscribeToApplyStateChanges();
    }

    // execute first time
    this.recalculateReadOnly();
    this.isRequired = this.recalculateRequired();
    this.required$.next(this.isRequired);

    // runResetOriginalValue sets the originalValue on the sohoxi component so that the validation/track dirty evaluation
    // can be made correctly
    this.runResetOriginalValue = true;          // indicates needs to run
    this.runResetOriginalValueInitial = true;   // indicates the first time run

    if (this.isCompoundFieldMember && !this.parentCompoundFieldDisplayOnly && this.isReadOnly) {
      if (this.modelField.isBlank) {
        this.displayLabelAsValue = true;
      }
    }

    if (this.isCompoundFieldMember && this.lmParagraphLayoutInfo && this.isPreviewCompoundMember) {
      if (this.modelField.isBlank) {
        this.displayLabelAsValue = true;
        // using non-strict equality to evaluate 0, '0', 0.00, '0.00' as zero, etc
        // eslint-disable-next-line eqeqeq
      } else if (this.specField.stateValues && this.modelField.value == 0) {
        this.displayLabelAsValue = this.specField.stateValues[0].label.trim() === '';
      }
    }

    if (this.isCompoundFieldMember && !!this.modelField.value && !this.modelField.isBlank) {
      this.showLabel = false;
    }

    if (this.layoutObject.exportFilterCriteria) {
      this.modelField.exportFilterCriteria = this.layoutObject.exportFilterCriteria;
    }

    this.evaluateTagStyle();
    this.setTransformOptions();

    this.subscriptionArray.push(this.model.modelChanged
      .subscribe((modelChangedEvent: LMModelChangedEvent) => {

        // initialize for each event
        this.checkApplyState = false;


        if ((this.hasMask && this.specField?.precision?.type === 'field')
        || (modelChangedEvent.source && modelChangedEvent.source.type === 'paging')
        || (modelChangedEvent.source?.type === 'refresh' && this.lmFieldServiceAdapter.lmFieldLayoutService.hasTimeZone(this.field, this.specField))) {
          this.setTransformOptions();

          if (this.hasMask && this.specField?.precision?.type === 'field') {
            this.setNumberFormat();
          }
        }

        this.evaluateTagStyle();

        if (modelChangedEvent.eventType === 'numberLabelChanged') {
          // this forces the numberLabel message rules to recalculate
          // the use case is template form child dynamic visibility
          this.triggerValue = !this.triggerValue;
          this.triggerValueChange.next(this.triggerValue);

        } else if (modelChangedEvent.eventType === 'set') {
          this.runResetOriginalValue = true;

          if (this.trackDirtyChildren && this.isDirty) {
            // Ensuring that the dirty indicator gets removed when the value returns to the original value.
            // Needs to be in a timeout so the soho control can update it's .data('original') before resetting the state.
            this.trackDirtyChildren.forEach((trackDirtyChild: SohoTrackDirtyDirective) => {
              this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
                setTimeout(() => trackDirtyChild.resetDirty(), 2);
              });
            });
          }

          if (this.sohoAlert && !this.model.isSearchForm) {
            // Search form doesn't do extra validation, so don't remove the messages for
            // required or invalid dates
            this.removeAllMessages();

            this.fieldMessage.error = undefined;
            this.fieldMessage.alert = undefined;

            this.processAlerts();
          }

          // re-evaluate
          this.recalculateReadOnly();
          this.isRequired = this.recalculateRequired();
          this.required$.next(this.isRequired);

          if (this.isCompoundFieldMember && !this.parentCompoundFieldDisplayOnly && this.isReadOnly) {
            this.displayLabelAsValue = this.modelField.isBlank;
          }

        } else if (modelChangedEvent.eventType === 'update') {
          this.checkApplyState = true;
        }

        if (modelChangedEvent.eventType === 'scannableChanged') {
          if (this.specField && this.specField.scannable) {
            const info: LMModelFieldInfo = modelChangedEvent.info;
          }
        }

        if (modelChangedEvent.eventType === 'set' || modelChangedEvent.eventType === 'update') {

          // triggerValue changes to indicate the formatMessageRule pipe to fire
          this.triggerValue = !this.triggerValue;
          this.triggerValueChange.next(this.triggerValue);

          // determine if the field options should be seen
          this.insertContextMenu = this.showContextMenu();

          // hide label on a compound field when a value is entered, show the label if the value is cleared
          this.processShowLabel();
        }

        // this.lmFieldServiceAdapter.changeDetectorRef.markForCheck();
      })
    );

    // validity conditions listen for evaluateConditions notification
    if (this.layoutObject.validity || this.layoutObject.displayAsRequired) {
      this.subscriptionArray.push(
        this.lmFieldServiceAdapter.lmConditionUtilService.evaluateConditions.subscribe(() => {
          this.recalculateReadOnly();
          this.isRequired = this.recalculateRequired();
          this.required$.next(this.isRequired);

          this.lmFieldServiceAdapter.changeDetectorRef.markForCheck();
        })
      );
    }

    // modelBase is a result of executing the initialize() method, so can call recalculateReadOnly()
    if (this.lmParagraphLayoutInfo?.isCompoundField && this.modelBase && this.isCompoundFieldMember) {
      this.recalculateReadOnly();
    }
  }

  protected handleOnFieldChanged() {
    // this.lmFieldServiceAdapter.lmLoggerService.log(['lm-field-base handleOnFieldChanged()', this.bindId]);
    this.hasFieldChanged = false;

    // hide label on a compound field when a value is entered, show the label if the value is cleared
    this.processShowLabel();

    if (this.modelField.fireInitialValue) {
      this.busyIndicator = true;
      this.fireInitialValueRuleEvent(this.bindId);

    } else if (this.specField.conditionalDisplaySource) {
      // will notify via the lmConditionUtilService.evaluateConditions() subscription
      this.fireEvaluateConditionsEvent(this.bindId);

    } else {
      this.insertContextMenu = this.showContextMenu();
    }

    if (this.model?.formSpec?.isTemplateForm) {
      // template dynamic child pattern needs to know when fields change
      this.fireTemplateFieldChangedEvent(this.bindId);
    }

    this.busyIndicator = false;
    this.evaluateTagStyle();
  }

  // called from a radiobutton click event
  // the sohoxi controls will run the validation on a click and will
  // remove the alert that was placed there, so need to re-apply the alert
  protected onChangeProcessAlerts() {
    if (this.fieldMessage.alert) {
      // although a click on a radiobutton will cause the alert to be removed, you can actually click on
      // the alert itself and it would add a duplicate alert, so playing it safe by removing all messages
      this.resetAlert(true);
    }
  }

  protected recalculateRequired(): boolean {
    const validityParams = this.validityParams;

    if (validityParams) {
      if (this.displayAsText || this.displayOnly || !validityParams.isMutable) {
        this._isRequired = false;

      } else {
        // strict equality due to this.modelField.required is tri-state
        if (this._isConfigRequired || validityParams.isRequired === true || validityParams.displayAsRequired === true) {
          this._isRequired = true;

        } else if (validityParams.isRequired === null || validityParams.isRequired === false || validityParams.displayAsRequired === false) {
          this._isRequired = false;
        }
      }
    }

    return this._isRequired;
  }

  protected recalculateReadOnly(): void {
    const parentDisplayOnly = (typeof this._parentCompoundFieldDisplayOnly === 'boolean') ? this._parentCompoundFieldDisplayOnly : null;

    // when parent compound field ia set to display only, it over-rides the child
    if (this.isCompoundFieldMember && parentDisplayOnly === true) {
      this.disabledMemberField = false;
      this.isReadOnly = true;
      this.readOnly$.next(this.isReadOnly);
      return;
    }

    const regularSearchForm = this.onSearchForm && !this.model.isCubeSearchForm;
    const validityParams = this.validityParams;
    if (validityParams) {
      if ((this.displayOnly || !validityParams.isMutable) && !regularSearchForm) {
        // return true sets readOnly attribute to true
        if (this.isCompoundFieldMember && !parentDisplayOnly) {
          // parent compound field isn't read only, but this member field is
          this.disabledMemberField = true;
        }
        this.isReadOnly = true;

        // Read only is evaluated, mark the spec field appropriately. Readonly information is used by search criteria and tags.
        if (this.model.isCubeSearchForm && this.isReadOnly && this.specField?.isBusClassContextField ) {
          // Context fields can be marked display only in a search form. Do it for cube search form for now.
          this.specField.displayOnly = true;
        }
        this.readOnly$.next(this.isReadOnly);
        return;
      }
    }

    if (this.specField?.allowUpdate && typeof this.specField?.allowUpdate === 'object') {
      this.isReadOnly = !this.lmFieldServiceAdapter.lmConditionUtilService.isValid([ this.specField.allowUpdate ], this.model);
      this.readOnly$.next(this.isReadOnly);
      return;
    }

    // return null removes readOnly attribute
    if (this.isCompoundFieldMember && !parentDisplayOnly) {
      this.disabledMemberField = false;
    }
    this.isReadOnly = false;
    this.readOnly$.next(this.isReadOnly);
  }

  protected setFieldClasses(size?: number) {
    if (this.layoutType === LMLayoutType.PROGRESS || !this.displayAsText) {

      const hasShowUpTo = (this.layoutObjectBase.showUpTo);
      const isEditor = (this.layoutType === LMLayoutType.ONE_LINE_TEXTAREA
        || this.layoutType === LMLayoutType.MULTI_LINE_TEXTAREA
        || this.layoutType === LMLayoutType.RICH_TEXT
        || this.layoutType === LMLayoutType.FIELD_EDITOR);

      // todo ppatton maf khollenbeck using size for now to determine which width class to use.
      if (size === undefined) {
        size = this.size;
      }

      if (hasShowUpTo) {
        size = this.layoutObjectBase.showUpTo;
      }

      //                       computed (border, padding, cell, padding, border)
      //    input-             lookup   (1, 10, 28, 35, 1)
      //    input-             text     (1, 10, 53, 10, 1)
      // dropdown-             dropdown (1, 10, 33, 30, 1)
      //                       # char px (~ # uppercase characters)
      //                          lookup       text   dropdown
      //         -xs =  75px -   3  28px    5  53px    2  33px
      //         -sm = 150px -  11 103px   14 128px   10 108px  also field-short
      //         -mm = 225px -  19 178px   21 203px   18 183px
      //         -md = 300px -  26 253px   29 278px   25 258px
      //         -lg = 400px -  38 353px   40 378px   36 358px
      //  input-full = 100%  -  xxxxxxxx   xxxxxxxx   xxxxxxxx

      if (this.model.isSearchForm && !hasShowUpTo) {
        if (isEditor) {
          this._fieldClassArray.push('input-mm');

        } else if (this.layoutType === LMLayoutType.DROPDOWN) {
          this._fieldClassArray.push('dropdown-sm');

        } else {
          this._fieldClassArray.push('input-sm');
        }

      } else if (this.isCompoundFieldMember) {
        if (this.layoutType === LMLayoutType.DATE) {
          this._fieldClassArray.push('input-sm');

        } else {
          this._fieldClassArray.push('input-xs');
        }

      } else if (this.layoutType === LMLayoutType.RADIO
        || this.layoutType === LMLayoutType.CHECKBOX
        || this.layoutType === LMLayoutType.CHECKCONTROL) {
        // no-op; do not set a width, but allow this.fieldClassesString to be created

      } else if (isEditor) {
        this._fieldClassArray.push('input-full');

      } else if (this.layoutType === LMLayoutType.PROGRESS) {
        this._fieldClassArray.push('input-mm');
        this._fieldClassArray.push('lm-progress');

      } else if (this.bindId === 'JobPostingSearchRel_col_KeywordSearch') {  // TODO do we want this hardcoded
        this._fieldClassArray.push('input-sm');

      } else if (this.specField?.type === AppConstants.TIMESTAMP) {
        if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
          this._fieldClassArray.push('input-sm');

        } else {
          this._fieldClassArray.push('input-mm');
        }

      } else if (this.specField?.type === AppConstants.MMDD) {
        this._fieldClassArray.push('input-sm');

      } else if (this.specField?.type === AppConstants.YYYY) {
        this._fieldClassArray.push('input-sm');

      } else if (this.layoutType === LMLayoutType.MULTI_SELECT) {
        this._fieldClassArray.push('dropdown-mm');

      } else if (this.layoutType === LMLayoutType.DROPDOWN && !this.specField?.hasUserDefinedStates) {
        if (size < 4) {
          this._fieldClassArray.push('dropdown-xs');

        } else if (size < 13) {
          this._fieldClassArray.push('dropdown-sm');

        } else if (size < 27) {
          this._fieldClassArray.push('dropdown-mm');

        } else if (size < 36) {
          this._fieldClassArray.push('dropdown-md');

        } else {
          this._fieldClassArray.push('dropdown-lg');
        }

        // TODO: need to evaluate incoming values and set the class appropriately; can only go larger, not smaller
      } else if (this.layoutType === LMLayoutType.DROPDOWN && this.specField?.hasUserDefinedStates && !this.layoutObjectBase.showUpTo) {
        this._fieldClassArray.push('dropdown-mm');

      } else if (this.layoutType === LMLayoutType.LOOKUP) {
        if (size < 3) {
          this._fieldClassArray.push('input-xs');

        } else if (size < 11) {
          this._fieldClassArray.push('input-sm');

        } else if (size < 19) {
          this._fieldClassArray.push('input-mm');

        } else if (size < 26) {
          this._fieldClassArray.push('input-md');

        } else {
          this._fieldClassArray.push('input-lg');
        }

      } else {
        if (size < 6) {
          this._fieldClassArray.push('input-xs');

        } else if (size < 20) {
          this._fieldClassArray.push('input-sm');

        } else if (size < 30) {
          this._fieldClassArray.push('input-mm');

        } else if (size < 50) {
          this._fieldClassArray.push('input-md');

        } else {
          this._fieldClassArray.push('input-lg');
        }
      }
    }

    this.fieldClassesString = this._fieldClassArray.join(' ');
  }

  // for lm-text and lm-lookup
  protected setNumberFormat() {
    if (this.specField.hasUserDefinedStates || this.specField.stateValues) {
      return;
    }

    if (this.model.isCubeSearchForm) {
      // Cube search form field value is a concatenation of ontological values separated by '/'
      return;
    }

    if (this.specField.type === AppConstants.BCD
      || this.specField.type === AppConstants.PERCENT
      || this.specField.type === AppConstants.NUMERIC) {

      const onlyAllowPositive = !(this.specField.signed);
      let allowDecimal: boolean = (this.specField.decSize !== undefined && this.specField.decSize > 0);
      let integerLimit: number = this.specField.size;
      let decimalLimit: number;
      let suffix: string;
      let showPercent: SohoMaskShowSymbol;
      const showThousands = (this.specField.type !== AppConstants.NUMERIC);

      if (allowDecimal) {
        let decSize = this.specField.decSize;

        // subtract two for the percent
        if (this.specField.type === AppConstants.PERCENT) {
          decSize -= 2;
        }

        // always use the smallest size for fraction
        let precision = this.modelField.precision;
        if (this.specField?.precision?.type === 'field' && this.model.getField(this.specField.precision.fieldId)) {
          precision = this.model.getField(this.specField.precision.fieldId).value;
        }

        const fractionDigits: number = (precision < decSize)
          ? precision
          : decSize;

        if (fractionDigits > 0) {
          decimalLimit = (this.field.numberOfDecimals || this.field.numberOfDecimals === 0) ? this.field.numberOfDecimals : fractionDigits;
        } else {
          allowDecimal = false;
        }

        integerLimit = this.specField.size - decSize;

        if (this.specField.type === AppConstants.PERCENT) {
          showPercent = 'percent';
          suffix = Soho.Locale.numbers().percentSign;
        }
      }

      // soho mask options
      this.hasMask = true;

      this.maskOptions = {
        process:        'number',
        showSymbol:     showPercent,
        patternOptions: {
          allowDecimal,
          allowNegative:           !onlyAllowPositive,
          allowThousandsSeparator: showThousands,
          integerLimit,
          decimalLimit,
          suffix,
          allowLeadingZeros:       true,
          symbols:                 {
            decimal:   Soho.Locale.numbers().decimal,
            negative:  Soho.Locale.numbers().minusSign,
            thousands: Soho.Locale.numbers().group
          }
        }
      };
    }
  }

  // for lm-text and lm-lookup
  protected setSearchFieldNumberFormat() {
    if (this.model.isCubeSearchForm) {
      // Cube search form field value is a concatenation of ontological values separated by '/'
      return;
    }

    if (!this.lmFieldServiceAdapter.lmContextService.useMultiSelectKeyField()) {
      this.setNumberFormat();
    }

    if (this.specField.hasUserDefinedStates || this.specField.stateValues) {
      return;
    }

    if (this.specField.type === AppConstants.BCD
      || this.specField.type === AppConstants.PERCENT
      || this.specField.type === AppConstants.NUMERIC) {

      const percentAdjustment: number = (this.specField.type === AppConstants.PERCENT) ? 2 : 0;
      const allowDecimal: boolean = (this.specField.decSize !== undefined && this.specField.decSize - percentAdjustment > 0);
      const decimalLimit: number = (allowDecimal) ? this.specField.decSize - percentAdjustment : 0;
      const integerLimit: number = (allowDecimal) ? this.specField.size - decimalLimit : this.specField.size;
      const decimal = allowDecimal ? Soho.Locale.numbers().decimal : '';
      const negative = this.specField.signed ? Soho.Locale.numbers().minusSign : '';
      const thousands = decimal ? Soho.Locale.numbers().group : '';
      const anyDigit = '\\d';
      const space = ' ';

      this.searchFieldMaskOptions = {
        // eslint-disable-next-line prefer-arrow/prefer-arrow-functions,no-unused-vars
        pattern: function customMask(rawValue, options) {
          const match = new RegExp(`[${negative}${decimal}${thousands}${space}${AppConstants.MULTI_SEARCH_TERM_DELIMITER}${anyDigit}]`);
          return rawValue.split('').map(() => match);
        }
      };
    }
  }

  protected setSize() {
    if (this.specField?.size) {
      if (this.maxLength === undefined) {
        this.maxLength = this.specField.size;
      }
    }

    if (this.specField?.dsplSize) {
      this.size = this.specField.dsplSize;
    }

    if (this.layoutType === LMLayoutType.COMPACT_FORMAT ||
      (this.layoutType === LMLayoutType.LOOKUP && this.modelField.isCompoundField)) {
      this.size = this.computeCompactFieldSize(this.modelField);
      this.maxLength = this.size;
    }
  }

  protected setTransformOptions() {
    this.transformOptions = {};

    if (this.isDisplayZero) {
      this.transformOptions.displayZero = !this.field.displayBlankWhenZero;
    }

    if (this.model.isCubeSearchForm) {
      this.transformOptions.doNotTransform = true;
    }

    // Disabling multiselect for cube search form for now.
    let allowMulti = this.lmFieldServiceAdapter.lmContextService.useMultiSelectKeyField() && this.model.isSearchForm && !this.model.isCubeSearchForm && !this.specField?.isBusClassContextField;
    if (allowMulti && this.specField) {
      allowMulti = this.lmFieldServiceAdapter.lmFieldUtilService.typeAllowsMultipleSearchTerms(this.specField.type);
    }
    this.transformOptions.allowMultiSelections = allowMulti;

    if (allowMulti) {
      this.numericFilterSettings.unshift({ value: 'contains', text: Soho.Locale.translate('Contains'), icon: 'filter-contains' });
      this.transformOptions.maintainSelectedValues = this.lmFieldServiceAdapter.lmContextService.enableMaintainingSelectedValues();
    }

    if (this.specField
      && (this.specField.type === AppConstants.BCD || this.specField.type === AppConstants.PERCENT)) {
      this.transformOptions.precision = (this.field.numberOfDecimals || this.field.numberOfDecimals === 0) ? this.field.numberOfDecimals : this.modelField.precision;
      this.transformOptions.decSize = (this.field.numberOfDecimals || this.field.numberOfDecimals === 0) ? this.field.numberOfDecimals : this.specField.decSize;

      if (this.field.displayNegativeAmountAs === 'parens') {
        this.transformOptions.displayNegativeParens = this.field.displayNegativeAmountAs;
      }

      if (this.field.showPercentSymbol) {
        this.transformOptions.showPercentSymbol = true;
      }

      if (this.field.hasCurrencySymbol) {
        this.transformOptions.currencySymbolIs =
          this.lmFieldServiceAdapter.lmFormatUtilService.getCurrencySymbol(this.field.currencySymbolInfo, this.model.getData());
      }
    }

    if (this.lmFieldServiceAdapter.lmFieldLayoutService.hasTimeZone(this.field, this.specField)) {
      this.transformOptions.timeZone = this.model.getField(this.field.timeZoneIs)?.value;
    }

    if (this.lmFieldServiceAdapter.lmFieldLayoutService.hasDisableDST(this.field, this.specField)) {
      this.transformOptions.disableDST = true;
    }

    if (this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayWithTimeZone(this.field, this.specField)) {
      this.transformOptions.displayWithTimeZone = this.field.displayWithTimeZone;
    }

    this.transformOptions.timeStampDirective = this.timeStampDirective;
    this.transformOptions.isCompoundField = this.modelField.isCompoundField;
  }

  protected setType() {
    this.type = (this.field.type === 'password') ? 'password' : 'text';
  }

  protected showContextMenu(): boolean {
    // only show the context menu if displayFieldHistory is true, and isSearchForm is false
    // displayFieldHistory || LayoutType is compactField
    // also hide the context menu when the LPL directive to disable field options exists
    if (this.onPageContextForm || this.model?.formSpec?.disableFieldOptions || this.layoutInfo?.disableFieldOptions) {
      return false;
    }

    return !this.lmFieldServiceAdapter.lmContextService.casualMode()
      && ((this.modelField.displayFieldHistory && !this.model.isCreateAction)
        || this.showDrillableContextMenu()
        || !!this.field.textVariables)
      && !this.onSearchForm && !this.isNumberLabel
      && !(this.lmParagraphLayoutInfo && this.isPreviewCompoundMember);
  }

  protected subscribeToInlineError() {
    // LMFieldSubscriptionService is different than the subscriptionArray
    // In LMFieldSubscriptionService, there is only one entry / subscription for a key as we only want one notification
    // to be emitted and not multiple subscriptions for multiple instances of a bindId on a form
    if (this.lmFieldServiceAdapter.lmFieldSubscriptionService.subscriptionAlreadyExists(this.fieldInlineError)) {
      this.lmFieldServiceAdapter.lmFieldSubscriptionService.add(this.fieldInlineError);

    } else {
      const inlineErrorSubscription = this.modelField.errorMessage.subscribe({
        next: (message) => {
          if (this.isCompoundFieldMember) {
            if (message !== '') {
              if (!this.isReadOnly) {
                this.onFocus(null);
              }
              this.compoundFieldInError = true;
              this.errorOnMemberField.emit(message);
            }
          }

          if (message !== '') {
            this.serverSideError = true;
          }

          this.processFieldMessage(message, true);
        }
      });

      this.lmFieldServiceAdapter.lmFieldSubscriptionService.add(this.fieldInlineError, inlineErrorSubscription);
    }
  }

  public getUserWebRequestId(): string {
    return 'FIELD-' +this.getBaseCompId();
  }

  // public methods
  // eslint-disable-next-line no-unused-vars
  public getComponentInfo(additionalInfo?: LMCompInfoAdditionalInfo): LMComponentInfo {

    const resourceInfo = this.field && this.field.resourceInfo ? this.field.resourceInfo : undefined;
    let bindId = this.lmFieldServiceAdapter.lmFieldUtilService.decodeFieldName(this.field.bindId);
    bindId = bindId.substring(0, 1) === '(' ? bindId.substring(1) : bindId;
    bindId = bindId.substring(bindId.length-1) === ')' ? bindId.substring(0, bindId.length-1) : bindId;
    let last = bindId.lastIndexOf(':');
    last = last < 0 ? bindId.lastIndexOf('.') + 1 : last + 1;

    const formInfo: LMComponentInfo = {
      componentType: 'FIELD',
      // TODO: need to provide hasError status
      busClassName:  this.model.formSpec.busClassSpec.name,
      module:        this.model.formSpec ? this.model.formSpec.busClassSpec.module : undefined,
      formName:      this.model.formSpec.busFormSpec.name,
      status:        this.getStatus({ isDirty: this.isDirty }),
      name:          bindId.substring(last),
      type :         'TEXT',
      fieldLayout:   JSON.stringify(this.field, null, 4),
      developer:     this.lmFieldServiceAdapter.lmContextService.getContext().developer,
      lpl:           this.field.lpl,
      parentLink:    bindId.split(':').join('.'),
      resourceInfo,
      hasConfigAccess: this.lmComponentInfoService.hasConfigAccess(),
    };

    if (this.specField) {
      formInfo.type = this.specField.type;
      formInfo.fieldSize = this.specField.size;
      formInfo.specField = JSON.stringify(this.specField, null, 4);

      // display the current stateValues from the model
      formInfo.stateValues = this.getStateFields();
    }

    return formInfo;
  }

  public getComponentPlaceHolder() {
    return this.placeHolder;
  }

  // If on a search form, make sure to clear out any fields in the data view that are multi-value.
  // If a value comes in as an array of one, grab the value out of the array as the new value
  public getSearchFormValidatedDataView(): LMDataView {
    if (this.model.isSearchForm) {
      const clonedDataView: LMDataView = JSON.parse(JSON.stringify(this.model.getData()));
      Object.keys(clonedDataView.fields).forEach((key) => {
        const field: LMModelField = clonedDataView.fields[ key ] as LMModelField;
        if(Array.isArray(field.value)) {
          if (field.value.length === 1) {
            field.value = field.value[ 0 ];
          } else {
            field.value = '';
          }
        } else if (typeof field.value === 'string' && (field.value as string).includes(',')) {
          field.value = '';
        }
      });
      return clonedDataView;
    }
    return this.model.getData();
  }

  // Stops the click event from propagating up when opening help text on mobile
  public mouseOverTextClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.tooltip.show();
  }

  public displayMouseOverText = (response: any) => {
    if (this.layoutObject.mouseOverText) {
      this.mouseOverTextContents = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.layoutObject.mouseOverText, this.model);
      response(this.mouseOverTextContents);
    }
  }

  // private methods
  private addInlineMessage(message: string, type: SohoAlertType, isAlert: boolean, icon?: string) {
    if (this.sohoAlert) {
      // If the sohoAlert doesn't have an id, it will attempt to use the message as the id. This can cause
      // issues since we sanitize the message that is placed in HTML, so encoded characters become displayed
      // characters. This is causing the inline error to not be reset correctly.
      // See https://jira.infor.com/browse/LMWEB-1462
      this.sohoAlert.id = this.bindId;
      this.sohoAlert.addInlineMessage(
        this.lmFieldServiceAdapter.domSanitizer.sanitize(SecurityContext.HTML, message),
        type,
        isAlert,
        false,
        icon
      );
    }
  }

  // NOTE: method is recursive
  private computeCompactFieldSize(modelField: LMModelField) {
    let addDelimiter = false;
    const delimiterLength = (modelField.groupFieldDelimiter || '').length;
    let size = 0;

    for (let index = 0, len = modelField.members.length; index < len; index++) {
      if (addDelimiter) {
        size += delimiterLength;
      }

      const memberId = modelField.members[ index ];
      const memberModelField = this.modelBase.getField(memberId);

      if (!memberModelField) {
        continue;
      }

      addDelimiter = (delimiterLength > 0);

      if (memberModelField.isCompoundField) {
        size += this.computeCompactFieldSize(memberModelField);

      } else {
        size += memberModelField.size;
      }
    }
    return size;
  }

  protected getInputElement() {
    return (this.inputElement && this.inputElement.nativeElement)
      ? this.inputElement.nativeElement
      : this.inputElement && this.inputElement[ 'element' ].nativeElement
        // SohoInputComponent
        ? this.inputElement[ 'element' ].nativeElement
        : undefined;
  }

  private getStateFields(): string {
    let retStateValues;
    if (this.specField && this.specField.stateValues) {
      retStateValues = JSON.stringify(this.specField.stateValues, null, 4);
    }

    if (this.modelField && this.modelField.states) {
      retStateValues = JSON.stringify(this.modelField.states, null, 4);
    }
    return retStateValues;
  }

  private getInputSelection(el) {
    let start = 0; let end = 0; let normalizedValue; let range;
    let textInputRange; let len; let endRange;

    if (typeof el.selectionStart === 'number' && typeof el.selectionEnd === 'number') {
      start = el.selectionStart;
      end = el.selectionEnd;
    } else {
      range = window.getSelection().getRangeAt(0);

      if (range && range.parentElement && range.parentElement() === el) {
        len = el.value.length;
        normalizedValue = el.value.replace(/\r\n/g, '\n');

        // Create a working TextRange that lives only in the input
        textInputRange = el.createTextRange();
        textInputRange.moveToBookmark(range.getBookmark());

        // Check if the start and end of the selection are at the very end
        // of the input, since moveStart/moveEnd doesn't return what we want
        // in those cases
        endRange = el.createTextRange();
        endRange.collapse(false);

        if (textInputRange.compareEndPoints('StartToEnd', endRange) > -1) {
          start = end = len;
        } else {
          start = -textInputRange.moveStart('character', -len);
          start += normalizedValue.slice(0, start).split('\n').length - 1;

          if (textInputRange.compareEndPoints('EndToEnd', endRange) > -1) {
            end = len;
          } else {
            end = -textInputRange.moveEnd('character', -len);
            end += normalizedValue.slice(0, end).split('\n').length - 1;
          }
        }
      } else {
        start = range.startOffset;
        end = range.endOffset;
      }
    }

    return {
      start,
      end
    };
  }

  private getModelField() {
    let modelField: any = null;

    modelField = this.modelBase.getField(this.bindId);

    if (!modelField) {
      modelField = this.modelBase.getDefaultField(this.bindId);
    }

    return modelField;
  }

  private processAlerts() {
    if (this.layoutObjectBase.alert) {
      this.hasAlert = true;
      let icon: string;
      let message = '';
      let messageRule: LMMessageRule = null;

      for (let index = 0, len = this.alerts.length; index < len; index++) {
        const item = this.alerts[ index ];
        const alertItem = this.layoutObjectBase[ item ];

        if (alertItem && this.lmFieldServiceAdapter.lmConditionUtilService.isValid(alertItem.condition, this.modelBase)) {
          messageRule = alertItem.alertMouseOverMessage;

          if (this.sohoAlert) {
            // only allow one message to be displayed
            this.removeAllMessages();

            if (messageRule) {
              message = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(messageRule, this.modelBase, this.specFieldsBase);
            }

            // cannot send a message text of blank, so make it one space so icon will display
            if (!message || message.length === 0) {
              message = ' ';
            }

            if (item === 'iconAlert') {
              icon = alertItem.icon;
            }

            const alertType: SohoAlertType = this.lmFieldServiceAdapter.lmCommonUtilService.convertAlertColorToAlertType(item) as SohoAlertType;

            this.fieldMessage.alert = {
              type:    alertType,
              message,
              icon
            };

            this.addInlineMessage(message, alertType, true, icon);

            // stop iterating the array
            break;
          }
        }
      }
    }
  }

  // called from onError and onValid events
  private processFieldMessage(message: string, fireFieldInvalidEvent?: boolean) {
    // this.lmFieldServiceAdapter.lmLoggerService.log([ 'processFieldMessage', this.bindId, message ]);

    if (!this.sohoAlert) {
      return;
    }

    if (message) {
      const currentMessage = this.sohoAlert.getMessage('error');

      // if messages are the same, then don't process again
      if (currentMessage === message) {
        return;
      }

      // don't call this.removeAllMessages() here
      this.sohoAlert.removeAllMessages(false);

      this.fieldMessage.error = {
        type:    'error',
        message
      };

      this.addInlineMessage(message, 'error', false);

      // do not do this for internal sohoxi errors, such as Invalid Date, etc
      if (fireFieldInvalidEvent === true) {
        // bubble up event, so the controlling form can know it's in error, and claim the field

        this.fieldEvent.emit(
          new LMInvalidFieldEvent(
            new CustomEvent('ValueChanged'), 'FieldInvalidEvent', this.bindId, this.sohoAlert));

        // if (this.inputElement) {
        //   let evt = document.createEvent('Event');
        //   evt.initEvent('focus', false, false);
        //   this.onFocus(evt);
        // }

      }

    } else if (message === '') {
      this.resetAlert();
    }
  }

  private removeAllMessages() {
    if (this.sohoAlert) {
      this.sohoAlert.removeAllMessages(false);
    }

    this.model.setError(this.bindId, false);
  }

  private resetAlert(skipCheck?: boolean) {
    // radiobuttons need to skip this check because message will be removed onClick
    if (skipCheck !== true && this.fieldMessage.alert) {
      const currentMessage = this.sohoAlert.getMessage(this.fieldMessage.alert.type);

      // if messages are the same, then don't process again
      if (currentMessage === this.fieldMessage.alert.message) {
        return;
      }
    }

    this.removeAllMessages();
    this.fieldMessage.error = undefined;
    // clear the blocking transaction if it exists. If the user is changing to an invalid date
    // the IDS validation will run after this and set the field in error again.
    this.model.setBlockTransactionError(this.bindId, false, null);
    this.compoundFieldInError = false;
    this.serverSideError = false;

    if (this.fieldMessage.alert) {
      this.addInlineMessage(this.fieldMessage.alert.message, this.fieldMessage.alert.type, true, this.fieldMessage.alert.icon);
    }
  }

  private selectElementContents(el) {
    if (window.getSelection && document.createRange) {
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      this.lmFieldServiceAdapter.lmLoggerService.log('Unable to reset selection range');
    }
  }

  private processShowLabel() {
    // hide label on a compound field when a value is entered, show the label if the value is cleared
    if (this.isCompoundFieldMember) {
      this.showLabel = !(!!this.modelField.value && !this.modelField.isBlank);
    }
  }

  private uniqueId(bindId: string): string {
    if (!bindId) {
      return null;
    }

    const formNamePrefix = this.lmFieldServiceAdapter.lmFormSpecUtilService.formNamePrefix(this.model.formSpec);
    const formName = this.lmFieldServiceAdapter.lmFormSpecUtilService.formName(this.model.formSpec);
    const instance = (this.field.hasOwnProperty('inst') && this.field.inst > 0) ? this.field.inst.toString() : undefined;
    const id = this.testVersion1 ? bindId : bindId.replace('_prd_', '_');
    return this.lmFieldServiceAdapter.lmUniqueIdService.uniqueId([ formNamePrefix, formName, id, instance ]);
  }

  protected uniqueFieldAutomationId(bindId: string): string {
    if (!bindId) {
      return null;
    }

    const instance = (this.field.hasOwnProperty('inst') && this.field.inst > 0) ? this.field.inst.toString() : undefined;
    const id = bindId.replace('_prd_', '-');
    return this.lmFieldServiceAdapter.lmUniqueIdService.uniqueAutomationId([ id, instance ]);
  }

  private showCompactFormContextMenu(): boolean {
    return (this.layoutType === LMLayoutType.COMPACT_FORMAT
      || this.layoutType === LMLayoutType.COMPACT_FORMAT_BY_FORM
      || (this.layoutType === LMLayoutType.LOOKUP && this.modelField.isCompoundField));
  }

  private showDrillableContextMenu(): boolean {
    if (!this.specField) {
      return false;
    }

    // drillable is true if the field has a value set on it, so make sure the value's length is greater than 0
    // (for alpha lookups)
    switch (this.specField.type) {
      case AppConstants.ALPHAUPPER:
      case AppConstants.ALPHALOWER:
      case AppConstants.ALPHARIGHT:
        return this.specField.drillable && this.modelField.getData().length > 0;

      default:
        return this.specField.drillable;
    }
  }

  private subscribeToApplyStateChanges() {
    const subscription = this.modelField.applyState.subscribe({
      next: () => {
        this.runApplyState = true;
      }
    });

    this.subscriptionArray.push(subscription);
  }

  private subscribeToFireInitialValueRuleCompleted() {
    // LMFieldSubscriptionService is different than the subscriptionArray
    // In LMFieldSubscriptionService, there is only one entry / subscription for a key as we only want one notification
    // to be emitted and not multiple subscriptions for multiple instances of a bindId on a form
    if (this.lmFieldServiceAdapter.lmFieldSubscriptionService.subscriptionAlreadyExists(this.fireInitialKey)) {
      this.lmFieldServiceAdapter.lmFieldSubscriptionService.add(this.fireInitialKey);

    } else {
      const fireInitialValueSubscription =
        this.lmFieldServiceAdapter.lmFormService.firingInitialValueRuleCompleted.subscribe(bindId => {
          if (bindId === this.bindId) {
            this.busyIndicator = false;
            this.insertContextMenu = this.showContextMenu();
            this.runAfterInitialValueRule = true;
          }
        });

      this.lmFieldServiceAdapter.lmFieldSubscriptionService.add(this.fireInitialKey, fireInitialValueSubscription);
    }
  }

  private getLMOperator(sohoOperator: SohoFilterFieldOperator | string): LMFilterFieldOperator {
    const foundEntry: OperatorMapping = AppConstants.OperatorMap.find(operatorEntry => operatorEntry.sohoOperator === sohoOperator);
    return foundEntry.lmOperator;
  }

  private buildFieldHistoryContent(): string {
    const tableStringStart =
      '<div class="hidden" id="popover-content">' +
      '<div style="height:auto;">' +
      '<table style="width:100%;">' +
      '<tbody>' +
      '<tr>' +
      '<th style="text-align:left; padding-left:10px; padding-right:10px">' +
      '<label>' + Soho.Locale.translate('EffectiveDate') + '</label>' +
      '</th>' +
      '<th style="text-align:left; padding-left:10px; padding-right:10px">' +
      '<label>' + Soho.Locale.translate('Value') + '</label>' +
      '</th>' +
      '<th style="text-align:left; padding-left:10px; padding-right:10px">' +
      '<label>' + Soho.Locale.translate('User') + '</label>' +
      '</th>' +
      '<th style="text-align:left; padding-left:10px; padding-right:10px">' +
      '<label>' + Soho.Locale.translate('Action') + '</label>' +
      '</th>' +
      '<th style="text-align:left; padding-left:10px; padding-right:10px">' +
      '<label>' + Soho.Locale.translate('Reason') + '</label>' +
      '</th>' +
      '</tr>';

    let tableString = '';
    let columnString = '';
    let valueString = '';
    let titleString = '';
    this.fieldHistoryInfo.reverse();
    this.fieldHistoryInfo.forEach((record: LMFieldHistoryResponse) => {
      tableString += '<tr>';
      //Effective Date
      columnString = Soho.Locale.translate('EffectiveDate');
      valueString = this.lmFieldServiceAdapter.lmFormatUtilService.yyyymmddToDisplay(record.effectiveDate);
      titleString = this.buildFieldHistoryTimestamp(record);
      tableString += '<td style="padding-left:10px; padding-right:10px">';
      tableString += '<label title="' + titleString + '" aria-label="' + columnString + ': ' + valueString + ', ' + titleString + '">' + valueString + '</label></td>';
      // Value
      tableString += '<td style="padding-left:10px; padding-right:10px"><label>' + record.auditValue + '</label></td>';
      // User
      columnString = Soho.Locale.translate('User');
      valueString = record.actorId;
      titleString = this.buildFieldHistoryActor(record);
      tableString += '<td style="padding-left:10px; padding-right:10px">';
      tableString += '<label title="' + titleString + '" aria-label="' + columnString + ': ' + valueString + ', ' + titleString + '">' + valueString + '</label></td>';
      //Action
      tableString += '<td style="padding-left:10px; padding-right:10px"><label>' + record?.action +'</label></td>';
      // //Reason
      tableString += '<td style="padding-left:10px; padding-right:10px"><label>' + record?.actionReason +'</label></td>';
      tableString += '</tr>';

    });
    const tableStringEnd = '</tr></tbody></table></div></div>';
    return tableStringStart + tableString + tableStringEnd;
  }

  private buildFieldHistoryTimestamp(record: LMFieldHistoryResponse): string {
    let detailString = Soho.Locale.translate('AppliedTime') + ': ';
    detailString += this.lmFieldServiceAdapter.lmFormatUtilService.timestampToDisplay(record.appliedStamp);

    return detailString;
  }

  private buildFieldHistoryActor(record: LMFieldHistoryResponse): string {
    let detailString = Soho.Locale.translate('ActorName') + ': ';
    detailString += record.actorName;

    return detailString;
  }

  private buildTextVariableInsertionMessage(message: string): string {
    return '<div class="hidden" id="popover-content">' +
      '<div style="height:auto;">' +
      '<table style="width:100%;">' +
      '<tr><td><label>' + message + '</label></td></tr>' +
      '</table></div></div>';
  }

  private evaluateTagStyle(): void {
    if (!this.displayAsTag) {
      return;
    }

    const value = (this.specField)
      ? this.lmFieldServiceAdapter.lmFormatUtilService.toDisplayValue(this.modelField.value, this.specField.type, this.transformOptions)
      : this.modelField.value;

    if (typeof value !== 'string' && typeof value !== 'boolean') {
      return;
    }

    this.tagStyle = null;
    if (this.displayAsTag) {
      if (this.tagStyleArray && this.tagStyleArray.length > 0) {
        this.tagStyle = this.tagStyleArray.find(tagStyle => {
          if (!tagStyle.condition) {
            return true;
          }

          return this.lmFieldServiceAdapter.lmConditionUtilService.isValid([ tagStyle.condition ], this.modelBase);
        });
      }
    }
  }

  protected openFieldOptions(event) {
    this.displayFieldOptions = true;
    const fieldOptionsButton = this.lmFieldServiceAdapter.elementRef.nativeElement.querySelector(`button#${this.fieldOptionsId}`);
    // fieldOptionsButton will be null if not found, so safety it
    if (fieldOptionsButton) {
      // Open up the field options menu, instead of browser context menu
      event.preventDefault();
      fieldOptionsButton.dispatchEvent(new Event('click'));
    }
  }
}
